import { type RefObject } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { usePilotInline } from "@/components/PilotLock";
import type { TabStyle } from "@/utils/types/ui";
import type { Variant, VariantStats } from "@/utils/types/variant";
import { FormulationTab } from "./FormulationTab";

type FormulationTabsBarProps = {
  variants: Variant[];
  variantStats?: Record<string, VariantStats>;
  activeVariantId: string;
  onActivate: (id: string) => void;
  onFork: () => void;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  // Kept optional so existing call sites (WorkbenchPage) can still pass
  // handlers without changes — the prompt-create surface is pilot-locked
  // for now and the values are ignored.
  onPromptOpen?: () => void;
  promptBtnRef?: RefObject<HTMLButtonElement | null>;
  tabStyle?: TabStyle;
  showKpi?: boolean;
  hideNew?: boolean;
  lockedVariantIds?: string[];
};

export function FormulationTabsBar({
  variants,
  variantStats = {},
  activeVariantId,
  onActivate,
  onFork,
  onRename,
  onRemove,
  tabStyle = "pill",
  showKpi = true,
  hideNew = false,
  lockedVariantIds = [],
}: FormulationTabsBarProps) {
  const activeStats = variantStats[activeVariantId];
  // Pilot-locked: prompt-based formulation creation is the click target,
  // not a wrapping div, so we use the inline-handler pattern from PilotLock
  // to keep the segmented look intact.
  const promptLock = usePilotInline();

  return (
    <div className={cn("tabs-bar", showKpi ? "with-kpi" : "no-kpi")}>
      {!hideNew && (
        <div className="tabs-new-split">
          <button
            type="button"
            onClick={onFork}
            title="Fork current flask into a new formulation"
            className="tabs-new-btn"
          >
            <Icon name="plus" size={11} />
            <span>New</span>
          </button>
          <span className="tabs-new-divider" />
          <button
            {...promptLock.bind}
            type="button"
            title="Generate a new formulation from a description — coming after Pilot"
            className="tabs-prompt-btn"
            aria-disabled
            style={{ opacity: 0.5, cursor: "not-allowed" }}
          >
            <Icon name="sparkle" size={11} />
            <Icon name="lock" size={9} />
          </button>
          {promptLock.tip}
        </div>
      )}

      <div className={cn("tabs-list", tabStyle === "minimal" && "minimal")}>
        {variants.map((v) => (
          <FormulationTab
            key={v.id}
            variant={v}
            active={v.id === activeVariantId}
            canRemove={variants.length > 1}
            style={tabStyle}
            showKpi={showKpi}
            stats={variantStats[v.id]}
            baseStats={activeStats}
            locked={lockedVariantIds.includes(v.id)}
            onClick={() => onActivate(v.id)}
            onRename={(name) => onRename(v.id, name)}
            onRemove={() => onRemove(v.id)}
          />
        ))}
      </div>
    </div>
  );
}
